import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  useTheme,
  Paper,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MarketingInfoBar from "./MarketingInfoBar";
import PageMeta from "./PageMeta";

const FEATURES = [
  {
    eyebrow: "Inventory",
    title: "Multi-cloud Inventory",
    body:
      "One place to manage every cloud asset — across providers, accounts, and SaaS integrations like Bitbucket, Git, and Okta. Get full visibility into asset history and real-time health.",
    bullets: [
      "Continuous discovery across clouds, accounts, and SaaS integrations",
      "Dependency graph auto-drawn; slice by account, env, team, or tag",
      "Full asset detail — ARN, config, tags, cost, policy state, history",
    ],
    image: "/images/inventory-image-light.png",
    imageDark: "/images/inventory-image-dark.png",
    alt: "Unified multi-cloud inventory with dependency graph",
  },
  {
    eyebrow: "Drift",
    title: "Drift Detection & Remediation",
    body:
      "Keeps your live cloud aligned with the IaC you thought you deployed. Drift surfaces the moment it appears — with a reviewable PR, not an auto-apply behind your back.",
    bullets: [
      "Continuously compares IaC against live state across every account",
      "Surfaces who, when, and what changed on every drifted resource",
      "Generates safe remediation as a reviewable PR — or optional auto-heal for guardrailed patterns",
    ],
    image: "/images/drift-remed-light.png",
    imageDark: "/images/drift-remed-image-dark.png",
    alt: "Drift detection comparing IaC to live cloud state",
  },
  {
    eyebrow: "Codify",
    title: "Codify Unmanaged Assets",
    body:
      "Close the IaC coverage gap in hours, not sprints. InfraBlaze turns live cloud resources into clean, reviewable IaC that merges into the stacks your team already runs.",
    bullets: [
      "One-click codify — turn live resources into IaC aligned with your conventions",
      "Import blocks drafted automatically — nothing to hand-edit",
      "Multi-IaC ready — Terraform today, OpenTofu and Pulumi on the roadmap",
    ],
    image: "/images/codify-iac-image-light.png",
    imageDark: "/images/codify-iac-image-dark.png",
    alt: "Codifying unmanaged cloud resources into IaC",
  },
  {
    eyebrow: "Workbench",
    title: "Visual Design Workbench",
    body:
      "Drag-and-drop design across AWS, Azure, and GCP on a single canvas. See cost and policy in-line as you design, then compile to reviewable IaC — before anyone opens the PR.",
    bullets: [
      "Design · Code · Estimate · Review — one surface, bidirectional sync",
      "Live cost preview (projected, direct, implied) on every change",
      "Policy checks run on the design, not just in CI",
    ],
    image: "/images/workbench-image-light.png",
    imageDark: "/images/workbench-image-dark.png",
    alt: "Visual infrastructure design workbench",
  },
  {
    eyebrow: "BlazeAI",
    title: "Specialized Agents Across Every Workflow",
    body:
      "BlazeAI isn't a chatbot bolted onto a dashboard. It's a suite of subagents — cost, naming, inventory, policy, insights — grounded in your live cloud. Ask questions in plain English; get answers, not dashboards.",
    bullets: [
      "Dedicated agents for codification, drift, cost, and policy",
      "Intent → query, diff, or ready-to-merge PR",
      "Claude · GPT · Bedrock — or BYO LLM with in-cluster data",
    ],
    image: "/images/agents-image-light.png",
    imageDark: "/images/agents-image-dark.png",
    alt: "BlazeAI specialized subagents for cloud operations",
  },
  {
    eyebrow: "Governance",
    title: "Policy-as-Code, Both Sides",
    body:
      "Policies evaluate against the design before any plan runs, and against the live cloud continuously. Evidence attaches to every PR — automatically.",
    bullets: [
      "SOC 2 + CIS + HIPAA out-of-the-box; OPA/Rego native, BYO policies supported",
      "Approver-gated merges; pass/fail evidence auto-attached to every PR",
      "Policy drift on the live cloud surfaces the moment it appears",
    ],
    image: "/images/policy-as-code-image-light.png",
    imageDark: "/images/policy-as-code-image-dark.png",
    alt: "Policy-as-code governance across design and live cloud",
  },
];

const DEPLOYMENT = [
  {
    title: "InfraBlaze Cloud",
    subtitle: "Managed SaaS",
    body: "Onboard quickly. Start with discovery and Auto‑IaC, then scale across accounts.",
  },
  {
    title: "InfraBlaze Enterprise Server",
    subtitle: "Self-hosted",
    body: "Deploy in your VPC for compliance and data residency. Keep inventory in your DB and plug in your preferred LLM.",
  },
];

const ProductPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const revealRefs = useRef([]);
  const [visibleRows, setVisibleRows] = useState(() => FEATURES.map(() => false));
  const [reducedMotion, setReducedMotion] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => setReducedMotion(mediaQuery.matches);

    updateMotionPreference();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateMotionPreference);
      return () => mediaQuery.removeEventListener("change", updateMotionPreference);
    }

    mediaQuery.addListener(updateMotionPreference);
    return () => mediaQuery.removeListener(updateMotionPreference);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setVisibleRows(FEATURES.map(() => true));
      return undefined;
    }

    if (typeof window === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const idx = Number(entry.target.getAttribute("data-feature-index"));
          if (!Number.isInteger(idx)) {
            return;
          }

          setVisibleRows((prev) => {
            if (prev[idx]) {
              return prev;
            }
            const next = [...prev];
            next[idx] = true;
            return next;
          });
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );

    revealRefs.current.forEach((node) => {
      if (node) {
        observer.observe(node);
      }
    });

    return () => observer.disconnect();
  }, [reducedMotion]);

  const SectionHeading = ({ eyebrow, title, subtitle, align = "left" }) => (
    <Stack spacing={1.2} sx={{ maxWidth: 980, mx: align === "center" ? "auto" : 0 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 2, fontWeight: 700, textAlign: align }}
      >
        {eyebrow}
      </Typography>
      <Typography
        variant="h3"
        sx={{ fontWeight: 900, letterSpacing: -0.5, lineHeight: 1.12, textAlign: align }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          color="text.secondary"
          sx={{ fontSize: 16, lineHeight: 1.9, textAlign: align }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
  );

  const FeatureRow = ({ item, reverse = false }) => (
    <Grid
      container
      spacing={{ xs: 4, md: 6 }}
      alignItems="center"
      direction={reverse ? "row-reverse" : "row"}
      sx={{ py: { xs: 5, md: 7 } }}
    >
      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.85),
            backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.55 : 0.95),
            backdropFilter: "blur(10px)",
            overflow: "hidden",
            boxShadow: isDark
              ? `0 0 0 1px ${alpha(
                  theme.palette.primary.main,
                  0.28
                )}, 0 18px 60px ${alpha(theme.palette.primary.main, 0.18)}`
              : `0 0 0 1px ${alpha(
                  theme.palette.primary.main,
                  0.18
                )}, 0 18px 60px ${alpha(theme.palette.primary.main, 0.12)}`,
            "&::before": {
              content: '""',
              position: "absolute",
              inset: -18,
              borderRadius: 12,
              background: isDark
                ? `radial-gradient(60% 60% at 30% 20%, ${alpha(
                    theme.palette.primary.main,
                    0.35
                  )}, transparent 60%), radial-gradient(55% 55% at 75% 25%, ${alpha(
                    theme.palette.secondary?.main || theme.palette.primary.main,
                    0.2
                  )}, transparent 65%)`
                : `radial-gradient(60% 60% at 30% 20%, ${alpha(
                    theme.palette.primary.main,
                    0.22
                  )}, transparent 60%), radial-gradient(55% 55% at 75% 25%, ${alpha(
                    theme.palette.secondary?.main || theme.palette.primary.main,
                    0.14
                  )}, transparent 65%)`,
              filter: "blur(18px)",
              opacity: isDark ? 0.6 : 0.45,
              zIndex: 0,
              pointerEvents: "none",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              background: `linear-gradient(180deg, ${alpha(
                theme.palette.background.default,
                isDark ? 0.18 : 0.08
              )}, transparent 28%, transparent 72%, ${alpha(
                theme.palette.background.default,
                isDark ? 0.2 : 0.1
              )})`,
              boxShadow: `inset 0 0 0 1px ${alpha(
                theme.palette.primary.main,
                isDark ? 0.12 : 0.08
              )}`,
              zIndex: 2,
              pointerEvents: "none",
            },
          }}
        >
          <Box
            component="img"
            src={isDark ? (item.imageDark || item.image) : item.image}
            alt={item.alt}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/saas.svg";
            }}
            sx={{ width: "100%", height: "auto", display: "block", position: "relative", zIndex: 1 }}
          />
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Stack spacing={2.2} sx={{ pr: { md: reverse ? 0 : 2 }, pl: { md: reverse ? 2 : 0 } }}>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: -0.4 }}>
            {item.title}
          </Typography>

          <Typography color="text.secondary" sx={{ fontSize: 16, lineHeight: 1.9 }}>
            {item.body}
          </Typography>

          <Stack spacing={1.3}>
            {item.bullets.map((b) => (
              <Stack key={b} direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    backgroundColor: alpha(theme.palette.primary.main, 0.9),
                    boxShadow: isDark
                      ? `0 0 14px ${alpha(theme.palette.primary.main, 0.22)}`
                      : `0 0 10px ${alpha(theme.palette.primary.main, 0.18)}`,
                    flex: "0 0 auto",
                  }}
                />
                <Typography sx={{ fontWeight: 650, lineHeight: 1.7 }}>{b}</Typography>
              </Stack>
            ))}
          </Stack>

        </Stack>
      </Grid>
    </Grid>
  );

  return (
    <Box
      component="main"
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        "@keyframes ibFloatPulse": {
          "0%, 100%": { transform: "translate(-50%, -50%) scale(1)", opacity: 0.6 },
          "50%": { transform: "translate(-50%, -52%) scale(1.06)", opacity: 0.85 },
        },
        "@keyframes ibDriftOrb": {
          "0%, 100%": { transform: "translate(0, 0)" },
          "50%": { transform: "translate(18px, -24px)" },
        },
      }}
    >
      <PageMeta
        title="Product — Control Plane for Agent Driven Infrastructure"
        description="The InfraBlaze control plane: multi-cloud inventory, visual design workbench, AI-generated IaC, drift detection and remediation, codification of unmanaged assets, BlazeAI subagents, and policy-as-code."
        keywords="control plane, agent driven infrastructure, InfraBlaze product, visual workbench, IaC generation, drift remediation, policy as code, multi-cloud"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "InfraBlaze Control Plane",
          "description": "Control plane for agent-driven infrastructure — visual multi-cloud workbench, IaC generation, drift remediation, and policy-as-code.",
          "brand": { "@type": "Brand", "name": "InfraBlaze" },
          "category": "Cloud Infrastructure Control Plane",
          "url": "https://www.infrablaze.com/product",
          "image": "https://www.infrablaze.com/images/hero-image-dark.png",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "url": "https://www.infrablaze.com/pricing"
          }
        }}
      />
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          pt: {
            xs: "calc(56px + env(safe-area-inset-top) + 48px)",
            md: "calc(56px + env(safe-area-inset-top) + 72px)",
          },
          pb: 0,
        }}
      >
        {/* Floating glow orbs */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "35%",
            left: "15%",
            width: { xs: 350, md: 650 },
            height: { xs: 350, md: 650 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)}, transparent 60%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
            zIndex: 0,
            animation: reducedMotion ? "none" : "ibFloatPulse 12s ease-in-out infinite",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "10%",
            right: "-5%",
            width: { xs: 300, md: 500 },
            height: { xs: 300, md: 500 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, isDark ? 0.18 : 0.10)}, transparent 65%)`,
            filter: "blur(70px)",
            pointerEvents: "none",
            zIndex: 0,
            animation: reducedMotion ? "none" : "ibDriftOrb 15s ease-in-out infinite",
          }}
        />
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}>
          <Stack spacing={3.5} alignItems="center" textAlign="center">
            <Typography
              variant="h1"
              sx={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? "translateY(0)" : "translateY(24px)",
                transition: "opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1), transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)",
                fontWeight: 950,
                letterSpacing: -1.0,
                lineHeight: 1.04,
                maxWidth: 900,
                fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.05rem" },
              }}
            >
              The Visual Cloud Operations Platform
            </Typography>

            <Typography
              variant="h6"
              color="text.primary"
              sx={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? "translateY(0)" : "translateY(24px)",
                transition: "opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1) 150ms, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) 150ms",
                lineHeight: 1.7,
                maxWidth: 780,
                fontWeight: 700,
                fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.4rem" },
              }}
            >
              Design, ship, and govern cloud infrastructure — visually, in one place.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{
                pt: 1,
                opacity: mounted ? 1 : 0,
                transform: mounted ? "translateY(0)" : "translateY(24px)",
                transition: "opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1) 300ms, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) 300ms",
              }}
            >
              <Button
                component="a"
                href="/contact"
                variant="contained"
                size="large"
                sx={{
                  fontWeight: 900,
                  borderRadius: 999,
                  px: 3.5,
                  color: "common.white !important",
                  boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.35)}`,
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    color: "common.white !important",
                    transform: "translateY(-2px)",
                    boxShadow: `0 14px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
                  },
                }}
              >
                Request a demo
              </Button>
              <Button
                component="a"
                href="/pricing"
                variant="outlined"
                size="large"
                sx={{
                  fontWeight: 900,
                  borderRadius: 999,
                  px: 3.5,
                  color: isDark ? "#fff" : "text.primary",
                  borderColor: alpha(isDark ? "#fff" : theme.palette.text.primary, 0.25),
                  transition: "transform 0.25s ease, border-color 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                View pricing
              </Button>
            </Stack>

          </Stack>
        </Container>
      </Box>

      {/* Image zone - transparent so the split starts exactly at image bottom */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}>
          <Box sx={{ position: "relative", width: "100%" }}>
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: -40,
                background: isDark
                  ? `radial-gradient(60% 60% at 50% 40%, ${alpha(
                      theme.palette.primary.main,
                      0.45
                    )}, transparent 65%),
                     radial-gradient(55% 55% at 70% 30%, ${alpha(
                       theme.palette.secondary?.main || theme.palette.primary.main,
                       0.35
                     )}, transparent 70%)`
                  : `radial-gradient(60% 60% at 50% 40%, ${alpha(
                      theme.palette.primary.main,
                      0.25
                    )}, transparent 65%),
                     radial-gradient(55% 55% at 70% 30%, ${alpha(
                       theme.palette.secondary?.main || theme.palette.primary.main,
                       0.18
                     )}, transparent 70%)`,
                filter: "blur(28px)",
                opacity: isDark ? 0.9 : 0.75,
                zIndex: 0,
              }}
            />

            <Paper
              elevation={0}
              sx={{
                position: "relative",
                zIndex: 1,
                maxWidth: 1100,
                height: { xs: 240, sm: 320, md: 460, lg: 500 },
                mb: 0,
                mx: "auto",
                borderRadius: "12px 12px 0 0",
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, isDark ? 0.45 : 0.35),
                backgroundColor: alpha(
                  theme.palette.background.paper,
                  isDark ? 0.65 : 0.95
                ),
                boxShadow: `0 0 0 1px ${alpha(
                  theme.palette.primary.main,
                  isDark ? 0.35 : 0.25
                )}, 0 30px 120px ${alpha(
                  theme.palette.primary.main,
                  isDark ? 0.45 : 0.25
                )}`,
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={isDark ? "/images/hero-image-dark.png" : "/images/hero-image-light.png"}
                alt="InfraBlaze product hero"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/saas.svg";
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "cover",
                  objectPosition: "top",
                }}
              />
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Feature narrative (alternating sections) */}
      <Box
        sx={{
          position: "relative",
          py: { xs: 7, md: 9 },
          background: isDark
            ? alpha("#000", 0.38)
            : alpha("#0a1220", 0.08),
          borderTop: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.45 : 0.35)}`,
          boxShadow: `inset 0 1px 0 ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.25)}, 0 -1px 30px ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.14)}`,
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}>
          <SectionHeading
            eyebrow="WHY INFRABLAZE"
            title={
              <>
                Everything you need to discover, codify, and control cloud infrastructure
              </>
            }
            subtitle="One system for inventory, Auto‑IaC, policy controls, and cost guardrails."
            align="center"
          />

          <Divider sx={{ my: { xs: 5, md: 7 }, opacity: 0.55 }} />

          {FEATURES.map((item, idx) => (
            <Box
              key={item.title}
              ref={(node) => {
                revealRefs.current[idx] = node;
              }}
              data-feature-index={idx}
              sx={{
                opacity: visibleRows[idx] ? 1 : 0,
                transform: visibleRows[idx] ? "translateY(0px)" : "translateY(42px)",
                transitionProperty: "opacity, transform",
                transitionDuration: "700ms",
                transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
                transitionDelay: `${idx * 90}ms`,
                willChange: "opacity, transform",
              }}
            >
              <FeatureRow item={item} reverse={idx % 2 === 1} />
            </Box>
          ))}
        </Container>
      </Box>

      {/* Deployment */}
      <Box sx={{ py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <SectionHeading
            eyebrow="DEPLOYMENT"
            title="Choose SaaS or Self‑Hosted"
            subtitle="Start with managed SaaS, or self-host for full data control and compliance."
            align="center"
          />

          <Grid container spacing={3} sx={{ mt: 2 }}>
            {DEPLOYMENT.map((d) => (
              <Grid key={d.title} item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.85),
                    backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.55 : 0.95),
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2 }}>
                      {d.subtitle}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, mt: 1 }}>
                      {d.title}
                    </Typography>
                    <Divider sx={{ my: 2, opacity: 0.6 }} />
                    <Typography color="text.secondary" sx={{ lineHeight: 1.85 }}>
                      {d.body}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.85),
              backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.55 : 0.95),
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: -24,
                pointerEvents: "none",
                background: isDark
                  ? `radial-gradient(60% 60% at 30% 20%, ${alpha(
                      theme.palette.primary.main,
                      0.22
                    )}, transparent 60%), radial-gradient(55% 55% at 80% 30%, ${alpha(
                      theme.palette.secondary?.main || theme.palette.primary.main,
                      0.14
                    )}, transparent 65%)`
                  : `radial-gradient(60% 60% at 30% 20%, ${alpha(
                      theme.palette.primary.main,
                      0.14
                    )}, transparent 60%), radial-gradient(55% 55% at 80% 30%, ${alpha(
                      theme.palette.secondary?.main || theme.palette.primary.main,
                      0.10
                    )}, transparent 65%)`,
                filter: "blur(18px)",
                opacity: isDark ? 0.7 : 0.55,
              }}
            />

            <Box sx={{ position: "relative", p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>
                    Turn live infrastructure into production‑ready IaC
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 1.25, lineHeight: 1.9 }}>
                    Discover assets, codify through PR workflows, enforce policy, and see cost impact before changes ship.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack direction={{ xs: "column", sm: "row", md: "column" }} spacing={1.25}>
                    <Button
                      component="a"
                      href="/contact"
                      variant="contained"
                      size="large"
                      sx={{
                        fontWeight: 900,
                        borderRadius: 999,
                        px: 3,
                        color: "common.white !important",
                        "&:hover": { color: "common.white !important" },
                      }}
                    >
                      Request a demo
                    </Button>
                    <Button
                      component="a"
                      href="/pricing"
                      variant="outlined"
                      size="large"
                      sx={{
                        fontWeight: 900,
                        borderRadius: 999,
                        px: 3,
                        color: isDark ? "#fff" : "text.primary",
                        borderColor: alpha(isDark ? "#fff" : theme.palette.text.primary, 0.25),
                      }}
                    >
                      View pricing
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Box sx={{ mt: "auto" }}>
        <MarketingInfoBar />
      </Box>
    </Box>
  );
};

export default ProductPage;
